import React, { useEffect, useMemo, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import bannerImg from "../../../assets/landing-page/alysei-bg.png";
import iconAi from "../../../assets/landing-page/icon-ai.png";
import useLocale from "../../../components/Hooks/useLocale";
import { urlWithLocalization } from "../../../components/LocalizationUrl/UrlWithLocalization";
import language from "../../../helpers/language";

const   NewBanner = () => {
  const history = useHistory();
  const [lang] = useState(language.getLang());
  const localeLocalization = useLocale();
  const [search, setSearch] = useState("");
  const [refine, setRefine] = useState("");
  const aiAssistantPlaceholders = useMemo(
    () => lang?.aiAssistantPlaceholders || ["What are you looking for today?"],
    [lang]  
  );
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [typedPlaceholder, setTypedPlaceholder] = useState("");
  const [isDeletingPlaceholder, setIsDeletingPlaceholder] = useState(false);

  useEffect(() => {
    const currentText = aiAssistantPlaceholders[placeholderIndex] || "";

    if (!isDeletingPlaceholder && typedPlaceholder.length === currentText.length) {
      const pauseTimer = setTimeout(() => setIsDeletingPlaceholder(true), 1200);
      return () => clearTimeout(pauseTimer);
    }

    if (isDeletingPlaceholder && typedPlaceholder.length === 0) {
      setIsDeletingPlaceholder(false);
      setPlaceholderIndex((currentIndex) => (currentIndex + 1) % aiAssistantPlaceholders.length);
      return undefined;
    }

    const typeTimer = setTimeout(() => {
      const nextLength = typedPlaceholder.length + (isDeletingPlaceholder ? -1 : 1);
      setTypedPlaceholder(currentText.slice(0, nextLength));
    }, isDeletingPlaceholder ? 24 : 45);

    return () => clearTimeout(typeTimer);
  }, [aiAssistantPlaceholders, isDeletingPlaceholder, placeholderIndex, typedPlaceholder]);

  const focusAssistantSearch = () => {
    const input = document.getElementById("ai-assistant-search-input");
    input?.scrollIntoView({ behavior: "smooth", block: "center" });
    window.setTimeout(() => input?.focus(), 450);
  };

  return (
    <div className="relative min-h-screen flex flex-col justify-center pt-4 md:pt-20 overflow-hidden">
      {/* Background image */}
      <img
        src={bannerImg}
        alt="Italian landscape"
        fetchpriority="high"
        decoding="async"
        className="absolute inset-0 w-full h-full object-cover"
      />
      {/* Dark overlay */}
      <div className="absolute inset-0" style={{ backgroundColor: "rgba(0,0,0,0.45)" }} />

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center text-center px-5 md:px-16 py-2 md:py-12">
        {/* Welcome pill */}
        <span className="inline-block bg-white/15 text-white text-xs font-medium px-4 py-1.5 rounded-md mb-6 backdrop-blur-md border border-white">
          {lang?.Welcome}
        </span>

        {/* Headline */}
        <h1 className="text-white text-3xl md:text-5xl lg:text-6xl font-bold leading-tight max-w-4xl mb-5">
          {lang?.SourcePremium}
        </h1>

        {/* Sub-headline */}
        <p className="text-white/90 text-sm md:text-base max-w-2xl mb-7">
          {lang?.GetMatched}
        </p>

        {/* CTA Buttons */}
        <div className="flex  gap-4 justify-center mb-10">
          <button
            type="button"
            onClick={focusAssistantSearch}
            className="px-4 md:px-12 py-3 bg-green-600 hover:bg-green-600 text-white font-semibold rounded-md transition-colors text-sm shadow-md"
          >
           {lang?.findProducers}
          </button>
          <Link
            to={urlWithLocalization(localeLocalization, "/register/italian-food-beverage-producers")}
            className="join-as-seller learn-more-link  items-center justify-between inline-flex px-4 md:px-7 py-3 bg-royal hover:opacity-90 text-white hover:text-white font-semibold rounded-md transition-opacity text-sm shadow-md"
          >
            {lang?.join_as_producers}
             <span className="learn-more-link__icon w-7 h-7 rounded-full bg-white flex items-center justify-center flex-shrink-0">
                  <i className="fa fa-arrow-right text-royal text-xs" />
                </span>
          </Link>
        </div>

        {/* Search Card */}
        <div className="w-full max-w-2xl">
          <p className="text-white text-xl font-semibold mb-3">
            Tell us what you are looking for
          </p>
          <p className="text-white text-sm mb-3">
            Describe your sourcing needs and we'll match you with relevant Italian producers.
          </p>

          <div id="ai-assistant-search" className="bg-white rounded-md shadow-2xl p-4 text-left mt-5">
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <div className="relative">
                  {!search && (
                    <div className="absolute inset-0 flex items-center pointer-events-none text-sm text-gray-600">
                      <span>{typedPlaceholder}</span>
                    </div>
                  )}
                <input
                  id="ai-assistant-search-input"
                  type="text"
                  placeholder=""
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && search.trim()) {
                      history.push("/chat", { initialMessage: search.trim() });
                    }
                  }}
                    className="w-full text-sm text-gray-800 placeholder-gray-400 outline-none bg-transparent relative z-10"
                    style={{ caretColor: 'auto' }}
                />
                </div>
              </div>
              <button
                onClick={() => {
                  if (search.trim()) {
                    history.push("/chat", { initialMessage: search.trim() });
                  }
                }}
                className="w-10 h-10 rounded-full bg-green-500 hover:bg-green-600 text-white flex items-center justify-center transition-colors flex-shrink-0"
              >
                <i className="fa fa-arrow-up text-sm" />
              </button>
            </div>
            <div className="border-t border-gray-100 mt-3 pt-3 flex items-center gap-2">
              {/* <img src={iconAi} alt="AI Icon" className="w-6 h-6" /> */}
              <div className="border-royal rounded-full border items-center justify-center p-1">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#004577" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className=" w-2.5 h-2.5 lucide lucide-sparkles-icon lucide-sparkles"><path d="M11.017 2.814a1 1 0 0 1 1.966 0l1.051 5.558a2 2 0 0 0 1.594 1.594l5.558 1.051a1 1 0 0 1 0 1.966l-5.558 1.051a2 2 0 0 0-1.594 1.594l-1.051 5.558a1 1 0 0 1-1.966 0l-1.051-5.558a2 2 0 0 0-1.594-1.594l-5.558-1.051a1 1 0 0 1 0-1.966l5.558-1.051a2 2 0 0 0 1.594-1.594z"/><path d="M20 2v4"/><path d="M22 4h-4"/><circle cx="4" cy="20" r="2"/></svg>
              </div>
              <span className="text-xs text-royal font-semibold">AI Assistant</span>
            </div>
          </div>

          <p className="text-white text-sm mt-4">
            We'll match you with 3–5 relevant producers!
          </p>
        </div>
      </div>
    </div>
  );
};

export default NewBanner;
